import {
  ACT, EAGLE, ENEMY, GRASSHOPPER, GROUND_ATTACK, NIGHT_SEC, NIGHTS, PLAYER,
  SPAWN_SHAPE, UNIT,
} from './config.js';

const TAU = Math.PI * 2;
const EPS = 1e-9;
const clamp01 = (v) => Math.max(0, Math.min(1, Number(v) || 0));

// Stable arbitration lanes. The order contains only the already-authored attack
// species; the moving cursor, not world-array order, chooses the next lane.
// No gameplay/species RNG is consumed and each scan remains O(N).
const MINOR_SPECIES_ORDER = Object.freeze([
  'field-rat', 'wolf', 'wild-dog', 'jackal', 'grasshopper', 'bat',
]);
const MAJOR_SPECIES_ORDER = Object.freeze(['wild-dog', 'crow', 'hyena']);

// The five entries are the same five runtime sections exposed by World.phase.
// They schedule behaviour, never births: species selection and spawn budgets stay in World.
export const BEHAVIOR_PHASES = Object.freeze([
  Object.freeze({ name: 'scout', majorCap: 1, jackal: true,  dog: false, bat: true, crow: false, hyena: true  }),
  Object.freeze({ name: 'main',  majorCap: 1, jackal: true,  dog: true,  bat: true, crow: true,  hyena: true  }),
  Object.freeze({ name: 'lull',  majorCap: 0, jackal: true,  dog: false, bat: true, crow: false, hyena: false }),
  Object.freeze({ name: 'wave',  majorCap: 1, jackal: true,  dog: true,  bat: true, crow: true,  hyena: true  }),
  Object.freeze({ name: 'tail',  majorCap: 1, jackal: true,  dog: false, bat: true, crow: true,  hyena: true  }),
]);

// Every duration, distance and speed below is derived from an already shipped combat
// ruler.  This module deliberately owns no second balance table: ACT supplies charge
// cadence/path, PLAYER supplies one-body prediction, and the authored N13 pack supplies
// the readable command size.  See enemy_behavior_system_check.mjs for the derivation audit.
const ONE_BODY_LEAD_SEC = UNIT / PLAYER.speed;
const COMMAND_MEMBERS = NIGHTS.N13.pack.n;
export const BEHAVIOR_TUNE = Object.freeze({
  hyena: Object.freeze({ recoverSec: PLAYER.iframe, retrySec: GRASSHOPPER.phaseSec }),
  jackal: Object.freeze({ pathDistance: ACT.charge.dist, recoverSec: PLAYER.iframe,
                          cooldownSec: ACT.charge.cool, speedMul: 1, leadSec: ONE_BODY_LEAD_SEC,
                          overshoot: GROUND_ATTACK.jackal.overshoot }),
  dog: Object.freeze({ maxMembers: COMMAND_MEMBERS, tellSec: ACT.charge.tell,
                       pathDistance: ACT.charge.dist, recoverSec: PLAYER.iframe,
                       cooldownSec: ACT.charge.cool, speedMul: 1, slotGap: UNIT,
                       leadSec: ONE_BODY_LEAD_SEC, staggerSec: GROUND_ATTACK.staggerSec }),
  bat: Object.freeze({ cycleSec: EAGLE.lowSec, forwardMul: 1, amplitude: UNIT }),
  crow: Object.freeze({ maxMembers: COMMAND_MEMBERS, tellSec: ACT.charge.tell,
                        pathDistance: ACT.charge.dist, recoverSec: PLAYER.iframe,
                        cooldownSec: ACT.charge.cool, speedMul: GRASSHOPPER.leapSpeedMul,
                        slotGap: UNIT, leadSec: ONE_BODY_LEAD_SEC,
                        staggerSec: GROUND_ATTACK.staggerSec }),
});

export function behaviorBandAt(t) {
  const p = clamp01((Number(t) || 0) / NIGHT_SEC);
  const ends = SPAWN_SHAPE.phaseEnds;
  if (p < ends[0]) return 0;
  if (p < ends[1]) return 1;
  if (p < ends[2]) return 2;
  if (p < ends[3]) return 3;
  return 4;
}

export function createBehaviorMetrics() {
  return {
    species: Object.create(null),
    spawn: 0, kill: 0, contact: 0, damage: 0, damageTaken: 0,
    start: 0, commit: 0, avoid: 0, cancel: 0, preempt: 0, recoverKill: 0,
    tokenGrants: 0, tokenDenied: 0, maxMajorTells: 0, majorTells: 0,
    minorGrants: 0, minorDenied: 0, minorPreempt: 0,
    minorAttacks: 0, maxMinorAttacks: 0,
    maxEnemyMajorTells: 0, priorityOverlapFrames: 0,
    majorSec: 0, enemyMajorSec: 0, eagleMajorSec: 0, bossMajorSec: 0,
    eagleDelayedFrames: 0, eagleDelaySec: 0, maxEagleDelaySec: 0, bossHeldSec: 0,
    eagleSignals: 0,
    standoffCorrections: 0, maxStandoffInsideContact: 0,
    bandSec: [0, 0, 0, 0, 0], bandStarts: [0, 0, 0, 0, 0],
    neighborChecks: 0,
  };
}

const behaviorKindFor = (species) => ({
  'field-rat': 'rat-bite-retreat',
  'wild-dog': 'dog-cohort',
  'jackal': 'jackal-crossing-snap',
  'grasshopper': 'grasshopper-rear-kick',
  'bat': 'bat-swoop-bite',
  'crow': 'crow-staggered-peck',
  'wolf': 'wolf-orbit-lunge',
  'wild-dog-leader': 'leader-command',
  'hyena': 'hyena-frontal-bite',
}[species] ?? 'straight');

function mix32(a) {
  a = Math.imul((a ^ (a >>> 16)) >>> 0, 0x7feb352d);
  a = Math.imul((a ^ (a >>> 15)) >>> 0, 0x846ca68b);
  return (a ^ (a >>> 16)) >>> 0;
}

function entitySeed(seed, nightNo, ordinal) {
  return mix32((seed | 0) ^ Math.imul((nightNo | 0) + 1, 0x9e3779b1)
    ^ Math.imul((ordinal | 0) + 1, 0x85ebca6b));
}

function nextEntityRandom(e) {
  let x = e._behaviorRngState >>> 0;
  x ^= x << 13; x ^= x >>> 17; x ^= x << 5;
  e._behaviorRngState = x >>> 0;
  return (x >>> 0) / 4294967296;
}

function norm(dx, dy, fx = 1, fy = 0) {
  const d = Math.hypot(dx, dy);
  return d > EPS ? { x: dx / d, y: dy / d, d } : { x: fx, y: fy, d: 0 };
}

// A tell may turn the actor's body toward the live player, but it must not
// publish or mutate the physical attack ray before the commit boundary.
function trackTellFacing(e, point) {
  const v = norm(point.x - e.x, point.y - e.y,
    e.bodyFacingDx ?? e.behaviorDx ?? 1,
    e.bodyFacingDy ?? e.behaviorDy ?? 0);
  e.bodyFacingDx = v.x; e.bodyFacingDy = v.y;
  return v;
}

function clearHiddenTellPath(e) {
  e._behaviorTellTargetX = null;
  e._behaviorTellTargetY = null;
  e._behaviorMaxTravel = 0;
}

function hideTellPath(e, maxTravel) {
  e._behaviorTellTargetX = e.behaviorTargetX;
  e._behaviorTellTargetY = e.behaviorTargetY;
  e._behaviorMaxTravel = Math.max(0, Number(maxTravel) || 0);
  e.behaviorTargetX = null;
  e.behaviorTargetY = null;
  e.bodyFacingDx = e.behaviorDx;
  e.bodyFacingDy = e.behaviorDy;
}

function revealStationaryTellPath(e) {
  e.behaviorTargetX = e._behaviorTellTargetX;
  e.behaviorTargetY = e._behaviorTellTargetY;
  clearHiddenTellPath(e);
}

function playerAtCommitBoundary(player, frameDt, elapsedBoundary) {
  const vx = Number.isFinite(player?.vx) ? player.vx : 0;
  const vy = Number.isFinite(player?.vy) ? player.vy : 0;
  if (Math.hypot(vx, vy) <= EPS) {
    return { x: player.x, y: player.y, moving: false };
  }
  const elapsed = Math.max(0, Math.min(Math.max(0, frameDt),
    Number(elapsedBoundary) || 0));
  const afterBoundary = Math.max(0, frameDt - elapsed);
  return {
    x: player.x - vx * afterBoundary,
    y: player.y - vy * afterBoundary,
    moving: true,
  };
}

export const enemyStandoffRadius = (e) =>
  PLAYER.r + Math.max(0, Number(e?.r) || 0) + GROUND_ATTACK.holdGap;

export function enemyNeedsStandoff(e) {
  if (!e || e.contactArmed || e.behaviorMajor || e.behaviorMinor
      || e.behaviorToken != null || e._grassAttackCycle || e._batAttackCycle) return false;
  return e.behaviorState !== 'tell' && e.behaviorState !== 'commit'
    && e.behaviorState !== 'recover';
}

export function enforceEnemyStandoff(e, player) {
  if (!enemyNeedsStandoff(e)) {
    if (e) e.holding = false;
    return false;
  }
  const hold = enemyStandoffRadius(e);
  const dx = e.x - player.x, dy = e.y - player.y;
  // An exact-centre recovery has no radial vector. Reuse the already seeded
  // per-entity state as an angle without advancing it, spreading simultaneous
  // arrivals deterministically instead of piling every fallback on one pixel.
  const d = Math.hypot(dx, dy);
  let ux, uy;
  if (d > EPS) {
    ux = dx / d; uy = dy / d;
  } else {
    const seededAngle = ((e._behaviorRngState ?? 0) >>> 0) / 4294967296 * TAU;
    ux = Math.cos(seededAngle); uy = Math.sin(seededAngle);
  }
  const corrected = d < hold - EPS;
  if (corrected) {
    e.x = player.x + ux * hold;
    e.y = player.y + uy * hold;
  }
  e.holding = d <= hold + EPS;
  e.holdRadius = hold;
  e.holdDx = ux; e.holdDy = uy;
  return corrected;
}

// One fallback implementation is shared by World and by the residual time left
// after an authored attack. Token-starved bodies stop one half-unit outside the
// contact circle and visibly queue there; circle actors orbit that same boundary.
// Both paths are O(1) per actor and never need neighbour scans.
export function moveEnemyFallback(e, dt, player) {
  const st = ENEMY[e.kind];
  e.behaviorState = 'fallback';
  e.contactArmed = false;
  enforceEnemyStandoff(e, player);
  let dx = player.x - e.x, dy = player.y - e.y;
  let d = Math.hypot(dx, dy);
  if (d <= EPS || dt <= 0) return;
  const ux = dx / d, uy = dy / d;
  const hold = enemyStandoffRadius(e);
  let moveDx = ux, moveDy = uy;
  if (st.act === 'circle' && d < ACT.circle.orbit) {
    const spin = e.behaviorSpin ?? 1;
    const tangent = ACT.circle.tangent;
    const l = Math.hypot(1 - tangent, tangent) || 1;
    const radialShare = (1 - tangent) / l;
    const lateralShare = tangent / l;
    let remaining = dt;
    let radius = d;
    let angle = 0;
    let atHold = radius <= hold + EPS;
    if (!atHold && radialShare > EPS) {
      const toHold = (radius - hold) / (st.speed * radialShare);
      const spiralSec = Math.min(remaining, Math.max(0, toHold));
      const endRadius = Math.max(hold, radius - st.speed * radialShare * spiralSec);
      angle += lateralShare * spin / radialShare * Math.log(endRadius / radius);
      radius = endRadius;
      remaining -= spiralSec;
      atHold = radius <= hold + EPS;
    }
    if (remaining > EPS) {
      // Once the safe ring is reached the unused inward component becomes
      // tangential speed, so the actor stages instead of squeezing inward.
      angle -= st.speed * spin * remaining / Math.max(EPS, radius);
    }
    const cos = Math.cos(angle), sin = Math.sin(angle);
    const rx = -ux, ry = -uy;
    const endRx = rx * cos - ry * sin;
    const endRy = rx * sin + ry * cos;
    e.x = player.x + endRx * radius;
    e.y = player.y + endRy * radius;
    const endTx = -endRy, endTy = endRx;
    if (atHold) {
      moveDx = -endTx * spin; moveDy = -endTy * spin;
    } else {
      moveDx = -endRx * radialShare - endTx * lateralShare * spin;
      moveDy = -endRy * radialShare - endTy * lateralShare * spin;
    }
  } else {
    const travel = Math.min(Math.max(0, d - hold), st.speed * dt);
    e.x += moveDx * travel;
    e.y += moveDy * travel;
  }
  e.behaviorDx = moveDx; e.behaviorDy = moveDy;
  d = Math.hypot(e.x - player.x, e.y - player.y);
  e.holding = d <= hold + EPS;
  e.holdRadius = hold;
}

export function eagleTalonGeometry(e, stage = 'signal') {
  const tune = GROUND_ATTACK.eagle;
  const facing = norm(e?.bodyFacingDx ?? e?.facing ?? 1,
    e?.bodyFacingDy ?? 0, e?.facing ?? 1, 0);
  const sideX = -facing.y, sideY = facing.x;
  const halfGap = tune.talonGap / 2;
  const capsules = [-halfGap, halfGap].map((offset, index) => ({
    id: index === 0 ? 'left' : 'right',
    x1: e.x - facing.x * tune.rakeBack + sideX * offset,
    y1: e.y - facing.y * tune.rakeBack + sideY * offset,
    x2: e.x + facing.x * tune.rakeReach + sideX * offset,
    y2: e.y + facing.y * tune.rakeReach + sideY * offset,
    r: tune.talonRadius,
  }));
  return {
    kind: 'talon-pair', stage, seq: e.behaviorSeq,
    armed: stage === 'commit', capsules,
  };
}

function clearGeometry(e) {
  e.behaviorTargetX = null;
  e.behaviorTargetY = null;
  e.behaviorDistance = 0;
  e._behaviorRemaining = 0;
  e._behaviorTravelled = 0;
  clearHiddenTellPath(e);
}

function lockPath(e, tx, ty, maxDistance = Infinity) {
  const v = norm(tx - e.x, ty - e.y, e.behaviorDx || 1, e.behaviorDy || 0);
  const distance = Math.max(0, Math.min(v.d, maxDistance));
  e.behaviorDx = v.x; e.behaviorDy = v.y;
  e.behaviorTargetX = e.x + v.x * distance;
  e.behaviorTargetY = e.y + v.y * distance;
  e.behaviorDistance = distance;
  e._behaviorRemaining = distance;
  e._behaviorTravelled = 0;
  return distance;
}

function lockCommitAimRay(e, player, frameDt, elapsedBoundary, maxTravel) {
  const boundary = playerAtCommitBoundary(player, frameDt, elapsedBoundary);
  if (!boundary.moving) {
    // Zero velocity preserves the pre-existing path byte-for-byte; only its
    // hidden tell endpoint becomes public at commit.
    revealStationaryTellPath(e);
    return false;
  }
  const facing = trackTellFacing(e, boundary);
  const travel = Math.max(0, Number(maxTravel) || 0);
  lockPath(e, e.x + facing.x * travel, e.y + facing.y * travel, travel);
  clearHiddenTellPath(e);
  return true;
}

export function recordBehaviorSweep(e, x1, y1, x2, y2,
                                    timeFrom = 0, timeTo = timeFrom,
                                    seq = e?.behaviorSeq, extra = null) {
  if (!e || Math.hypot(x2 - x1, y2 - y1) <= EPS) return;
  const segment = { seq, x1, y1, x2, y2, timeFrom, timeTo, ...(extra || {}) };
  if (!e.behaviorSweep) {
    e.behaviorSweep = { ...segment, segments: [segment] };
  } else {
    e.behaviorSweep.segments ??= [{
      seq: e.behaviorSweep.seq,
      x1: e.behaviorSweep.x1, y1: e.behaviorSweep.y1,
      x2: e.behaviorSweep.x2, y2: e.behaviorSweep.y2,
      timeFrom: e.behaviorSweep.timeFrom, timeTo: e.behaviorSweep.timeTo,
    }];
    e.behaviorSweep.segments.push(segment);
    e.behaviorSweep.seq = seq;
    e.behaviorSweep.x2 = x2; e.behaviorSweep.y2 = y2;
    e.behaviorSweep.timeTo = timeTo;
  }
}

function lockedMove(e, distance, timeFrom = 0, timeTo = timeFrom, extra = null) {
  const move = Math.min(Math.max(0, distance), Math.max(0, e._behaviorRemaining || 0));
  const fromX = e.x, fromY = e.y;
  e.x += (e.behaviorDx || 0) * move;
  e.y += (e.behaviorDy || 0) * move;
  recordBehaviorSweep(e, fromX, fromY, e.x, e.y, timeFrom, timeTo,
    e.behaviorSeq, extra);
  e._behaviorRemaining = Math.max(0, (e._behaviorRemaining || 0) - move);
  e._behaviorTravelled = Math.min(e.behaviorDistance,
    (e._behaviorTravelled || 0) + move);
  e.behaviorProgress = e.behaviorDistance > EPS
    ? clamp01(e._behaviorTravelled / e.behaviorDistance) : 1;
  return move;
}

export class EnemyBehaviorDirector {
  constructor({ seed = 7, nightKey = null, metrics = createBehaviorMetrics() } = {}) {
    this.seed = seed | 0;
    this.nightNo = Number(String(nightKey ?? '').slice(1)) || 0;
    this.metrics = metrics;
    this.spawnOrdinal = 0;
    this.tokens = new Map();
    this.minorTokens = new Map();
    this.cohorts = new Map();
    this.activeEnemies = new Set();
    this.activeEagles = new Set();
    this.openCohort = { crow: null };
    this.openDogCohorts = [];
    this.nextCohortId = 1;
    this.band = 0;
    this.profile = BEHAVIOR_PHASES[0];
    this.bossPriority = false;
    this.eaglePriority = 0;
    this.eagleWarningCount = 0;
    this.majorCapacity = 1;
    this.minorCapacity = GROUND_ATTACK.localCap;
    this.minorSpeciesCursor = this.nightNo % MINOR_SPECIES_ORDER.length;
    this.majorSpeciesCursor = this.nightNo % MAJOR_SPECIES_ORDER.length;
    this.minorFrameGrants = new Set();
    this.minorArbitrationFrame = -1;
    this.majorFrameGrants = new Set();
    this.majorArbitrationFrame = -1;
    this.enemyMajorActive = false;
    this.majorRecoveryActive = false;
    this.frameDt = 0;
    this.frameSerial = 0;
    this.now = 0;
  }

  stat(subject) {
    const species = typeof subject === 'string'
      ? subject : subject?.species ?? subject?.kind ?? 'unknown';
    return this.metrics.species[species] ??= {
      spawn: 0, kill: 0, contact: 0, damage: 0, damageTaken: 0,
      start: 0, commit: 0, avoid: 0, cancel: 0, preempt: 0, recoverKill: 0,
    };
  }

  count(subject, key, amount = 1) {
    const n = Number.isFinite(Number(amount)) ? Number(amount) : 0;
    this.stat(subject)[key] = (this.stat(subject)[key] ?? 0) + n;
    this.metrics[key] = (this.metrics[key] ?? 0) + n;
  }

  initializeEnemy(e, now = 0) {
    if (e._behaviorInitialized) return e;
    const ordinal = this.spawnOrdinal++;
    e._behaviorInitialized = true;
    e._behaviorAlive = false;
    e._behaviorOrdinal = ordinal;
    e._behaviorRngState = entitySeed(this.seed, this.nightNo, ordinal) || 0x6d2b79f5;
    e.behaviorKind = behaviorKindFor(e.species);
    e.behaviorState = e.behaviorKind === 'straight' ? 'fallback' : 'approach';
    e.behaviorProgress = 0;
    e.behaviorSeq = 0;
    e.behaviorToken = null;
    e.behaviorMajor = false;
    e.behaviorMinor = false;
    e.attackKind = null;
    e.attackStage = null;
    e.holding = false; e.holdRadius = null; e.holdDx = null; e.holdDy = null;
    e.attackOriginX = null; e.attackOriginY = null;
    e.bodyFacingDx = null; e.bodyFacingDy = null;
    e.behaviorDx = 1; e.behaviorDy = 0;
    e.behaviorTargetX = null; e.behaviorTargetY = null; e.behaviorDistance = 0;
    // Every ordinary body is harmless while approaching. Damage belongs only to
    // a committed bite/peck/kick path, so the silhouette and the hit window are
    // the same event instead of permanent contact damage with decorative motion.
    e.contactArmed = false;
    e.behaviorAnchored = false;
    e.behaviorConnected = false;
    e.behaviorCommitted = false;
    e.behaviorContactSeq = null;
    e.behaviorSweep = null;
    e.behaviorSpin = nextEntityRandom(e) < 0.5 ? -1 : 1;
    e.behaviorNextAt = now + nextEntityRandom(e) * ACT.charge.cool;
    e.cohortId = null; e.cohortRole = null; e.cohortSlot = null;
    e.cohortCommand = null;
    e.cohortCommandSeq = null;
    e.laneId = null; e.laneSide = null;
    e.waveSide = nextEntityRandom(e) < 0.5 ? -1 : 1;
    e.waveT = nextEntityRandom(e) * BEHAVIOR_TUNE.bat.cycleSec;
    e.wavePhase = e.waveT / BEHAVIOR_TUNE.bat.cycleSec * TAU;
    e._batNeedsCycleStart = false;
    e._batBasePrelocked = false;
    e._batCycleReady = false;
    e._batAttackCycle = false;
    e._batCommitCounted = false;
    e._waveBaseDx = 1; e._waveBaseDy = 0;
    return e;
  }

  admitEnemy(e, now = 0) {
    this.initializeEnemy(e, now);
    if (e._behaviorAlive) return e;
    e._behaviorAlive = true;
    this.activeEnemies.add(e);
    this.count(e, 'spawn');
    if (e.species === 'wild-dog' || e.species === 'wild-dog-leader') {
      this.joinCohort(e, 'dog', BEHAVIOR_TUNE.dog, now);
    } else if (e.species === 'crow') {
      this.joinCohort(e, 'crow', BEHAVIOR_TUNE.crow, now);
    }
    return e;
  }

  admitEagle(e) {
    e.behaviorKind = 'eagle-dive';
    e.behaviorState = 'tell';
    e.behaviorProgress = 0;
    e.behaviorSeq = 1;
    // The high-altitude shadow is a local warning, not a second screen-wide
    // major tell.  The authored low dive is where the global warning begins.
    e.behaviorToken = null;
    e.behaviorMajor = false;
    e.behaviorDx = 0; e.behaviorDy = 0;
    e.behaviorTargetX = e.x; e.behaviorTargetY = e.y; e.behaviorDistance = 0;
    e.contactArmed = false;
    e.behaviorCommitted = false; e.behaviorConnected = false;
    e.behaviorContactSeq = null;
    e.attackKind = 'talon-rake'; e.attackStage = 'approach';
    e.bodyFacingDx = e.facing ?? 1; e.bodyFacingDy = 0;
    e.talonSignal = false; e.talonSignalProgress = 0;
    e._talonSignalSeen = false;
    e.talonContactSpent = false; e.attackGeometry = null;
    e._talonCommitFresh = false; e._talonCommitAtT = null;
    e._behaviorAlive = true;
    this.activeEagles.add(e);
    this.count(e, 'spawn');
    this.count(e, 'start');
  }

  syncEagle(e) {
    if (!e) return;
    e.attackKind = 'talon-rake';
    e.bodyFacingDx = e.facing ?? e.bodyFacingDx ?? 1;
    e.bodyFacingDy = 0;
    if (e.state === 'high') {
      e.behaviorState = 'tell';
      e.behaviorMajor = false; e.behaviorToken = null;
      e.behaviorProgress = clamp01(e.stateT / EAGLE.warningSec);
      e.attackStage = 'approach';
      e.talonSignal = false; e.talonSignalProgress = 0;
      e.attackGeometry = null;
      e.contactArmed = false;
    } else if (e.state === 'low') {
      e.behaviorState = 'tell'; e.behaviorMajor = true; e.behaviorToken = 'eagle';
      e.behaviorProgress = clamp01((EAGLE.highSec + e.stateT) / EAGLE.warningSec);
      e.attackStage = 'tell'; e.contactArmed = false;
      e.talonSignal = e.stateT + EPS >= GROUND_ATTACK.eagle.signalSec;
      e.talonSignalProgress = e.talonSignal
        ? clamp01((e.stateT - GROUND_ATTACK.eagle.signalSec)
          / Math.max(EPS, EAGLE.lowSec - GROUND_ATTACK.eagle.signalSec)) : 0;
      if (e.talonSignal && !e._talonSignalSeen) {
        e._talonSignalSeen = true;
        this.metrics.eagleSignals++;
      }
      e.attackGeometry = e.talonSignal ? eagleTalonGeometry(e, 'signal') : null;
    } else if (e.state === 'grounded') {
      const fresh = !!e._talonCommitFresh;
      e.behaviorState = fresh ? 'commit' : 'recover';
      e.attackStage = fresh ? 'commit' : 'recover';
      e.behaviorMajor = false; e.behaviorToken = fresh ? 'eagle' : null;
      e.behaviorProgress = fresh ? 1 : clamp01(e.stateT / EAGLE.groundSec);
      e.talonSignal = false; e.talonSignalProgress = 1;
      e.attackGeometry = eagleTalonGeometry(e, fresh ? 'commit' : 'recover');
      e.contactArmed = false;
    } else {
      e.behaviorState = 'recover'; e.behaviorMajor = false; e.behaviorToken = null;
      e.behaviorProgress = clamp01(e.stateT / EAGLE.takeoffSec);
      e.attackStage = 'recover';
      e.talonSignal = false; e.talonSignalProgress = 0;
      e.attackGeometry = null;
      e.contactArmed = false;
    }
  }

  recordKill(e, now = this.now) {
    this.now = Math.max(this.now, Number(now) || 0);
    this.count(e, 'kill');
    if (e?.behaviorState === 'recover') this.count(e, 'recoverKill');
    this.removeEnemy(e);
  }

  recordContact(e, damage = 0, seq = e?.behaviorSeq) {
    this.count(e, 'contact');
    this.count(e, 'damage', Math.max(0, Number(damage) || 0));
    if (!e) return;
    e._behaviorAvoidPending?.delete(seq);
    if (seq === e.behaviorSeq && (e.behaviorCommitted
        || e.behaviorState === 'commit')) e.behaviorConnected = true;
  }

  contactAvailable(e, seq = e?.behaviorSeq) {
    if (!e || e.behaviorContactSeq === seq) return false;
    const cohort = e.cohortId == null ? null : this.cohorts.get(e.cohortId);
    if (cohort && e.cohortCommandSeq === cohort.commandSeq && cohort.contactSpent) {
      return false;
    }
    return true;
  }

  markBehaviorContact(e, seq = e?.behaviorSeq) {
    if (!e) return;
    e.behaviorContactSeq = seq;
    const cohort = e.cohortId == null ? null : this.cohorts.get(e.cohortId);
    if (cohort && e.cohortCommandSeq === cohort.commandSeq) {
      cohort.contactSpent = true;
      for (const member of cohort.members) {
        if (member.cohortCommandSeq === cohort.commandSeq) {
          member.behaviorContactSeq = member.behaviorSeq;
        }
      }
    }
  }

  recordDamageTaken(e, damage = 0) {
    this.count(e, 'damageTaken', Math.max(0, Number(damage) || 0));
  }

  completeCommit(e) {
    if (!e?.behaviorCommitted) return;
    if (!e.behaviorConnected) {
      const segments = e.behaviorSweep?.segments
        ?? (e.behaviorSweep ? [e.behaviorSweep] : []);
      if (segments.some((segment) => segment.seq === e.behaviorSeq
          && segment.armed !== false)) {
        e._behaviorAvoidPending ??= new Set();
        e._behaviorAvoidPending.add(e.behaviorSeq);
      } else {
        this.count(e, 'avoid');
      }
    }
    e.behaviorCommitted = false;
    e.behaviorConnected = false;
  }

  resolveFrameContacts() {
    for (const e of this.activeEnemies) {
      if (!e._behaviorAvoidPending?.size) continue;
      for (const seq of e._behaviorAvoidPending) {
        if (e.behaviorContactSeq !== seq) this.count(e, 'avoid');
      }
      e._behaviorAvoidPending.clear();
    }
  }

  removeEnemy(e) {
    if (!e) return;
    const cohort = e.cohortId == null ? null : this.cohorts.get(e.cohortId);
    // Killing the dog leader is the counter to its command. The command dies with
    // it; survivors keep their current position and enter a harmless recovery.
    if (cohort?.type === 'dog' && cohort.active && cohort.leader === e) {
      this.cancelCohort(cohort, this.now, { recover: true });
    }
    e._behaviorAlive = false;
    this.activeEnemies.delete(e);
    this.activeEagles.delete(e);
    this.release(e);
    this.releaseMinor(e);
    if (e.cohortId != null) {
      if (cohort) {
        cohort.members.delete(e);
        if (cohort.leader === e) cohort.leader = null;
        if (cohort.primary === e && cohort.active) {
          const promoted = [...cohort.members].find((member) => member._behaviorAlive
            && (member.behaviorTargetX != null
              || member._behaviorTellTargetX != null)
            && (member.behaviorState === 'tell' || member.behaviorState === 'commit'));
          if (promoted) {
            promoted.behaviorMajor = promoted.behaviorState === 'tell';
            cohort.primary = promoted;
          } else {
            cohort.primary = null;
            // No remaining readable lane needs the warning token. Committed or
            // recovering members still finish their physical sequence.
            this.release(cohort);
          }
        }
        if (!cohort.members.size) {
          if (cohort.type === 'crow' && this.openCohort.crow === cohort) {
            this.openCohort.crow = null;
          }
          this.release(cohort);
          this.cohorts.delete(cohort.id);
        }
      }
    }
  }

  reset(enemies = []) {
    for (const e of enemies) this.removeEnemy(e);
    this.tokens.clear();
    this.minorTokens.clear();
    this.cohorts.clear();
    this.activeEnemies.clear();
    this.activeEagles.clear();
    this.openCohort.crow = null;
    this.openDogCohorts.length = 0;
    this.minorFrameGrants.clear();
    this.minorArbitrationFrame = -1;
    this.majorFrameGrants.clear();
    this.majorArbitrationFrame = -1;
  }

  joinCohort(e, type, tune, now) {
    const isLeader = e.species === 'wild-dog-leader';
    let cohort = null;
    if (type === 'dog' && !isLeader) {
      while (this.openDogCohorts.length) {
        const first = this.openDogCohorts[0];
        if (this.cohorts.has(first.id) && !first.active && first.leader?._behaviorAlive
            && first.members.size < tune.maxMembers) {
          cohort = first;
          break;
        }
        this.openDogCohorts.shift();
      }
      // Uncommanded dogs keep the shipped straight fallback until a leader exists.
      if (!cohort) return;
    } else if (type === 'crow') {
      cohort = this.openCohort.crow;
    }
    if (!cohort || cohort.active || cohort.members.size >= tune.maxMembers) {
      const id = this.nextCohortId++;
      cohort = { id, type, members: new Set(), leader: null, active: false,
                 nextAt: now + nextEntityRandom(e) * ACT.charge.cool,
                 laneSide: nextEntityRandom(e) < 0.5 ? -1 : 1 };
      this.cohorts.set(id, cohort);
      if (type === 'crow') this.openCohort.crow = cohort;
      else this.openDogCohorts.push(cohort);
    }
    e.cohortId = cohort.id;
    e.cohortSlot = cohort.members.size;
    e.cohortRole = isLeader ? 'leader' : 'member';
    if (type === 'crow') {
      e.laneId = cohort.id;
      e.laneSide = cohort.laneSide;
    }
    cohort.members.add(e);
    if (isLeader) cohort.leader = e;
    if (cohort.members.size >= tune.maxMembers) {
      if (type === 'crow' && this.openCohort.crow === cohort) this.openCohort.crow = null;
      if (type === 'dog' && this.openDogCohorts[0] === cohort) this.openDogCohorts.shift();
    }
  }

  beginFrame(world, dt) {
    this.frameDt = Math.max(0, Number(dt) || 0);
    this.frameSerial++;
    this.now = Math.max(0, Number(world.t) || 0);
    // Safety for headless callers that advanced movement without running contact().
    this.resolveFrameContacts();
    this.reconcile(world);
    for (const e of world.enemies ?? []) e.behaviorSweep = null;
    const nextBand = behaviorBandAt(world.t);
    if (nextBand !== this.band) this.band = nextBand;
    this.profile = BEHAVIOR_PHASES[this.band];
    this.metrics.bandSec[this.band] += Math.max(0, Number(dt) || 0);
    // Cooldowns advance once before any entity gets to request a token. Major
    // candidates are then resolved for the whole frame, so array order cannot
    // let a rat consume 0.25s and bite before a ready hyena later in the array
    // publishes its higher-priority tell.
    for (const e of world.enemies ?? []) {
      if (e.species !== 'hyena') continue;
      e.cool = (e.cool ?? 0) - this.frameDt;
      e._hyenaCoolFrame = this.frameSerial;
    }
    this.bossPriority = this.hasBossPriority(world);
    this.eagleWarningCount = (world.eagles ?? [])
      .filter((e) => e.state === 'low').length;
    for (const eagle of world.eagles ?? []) this.syncEagle(eagle);
    // High/low are warnings; only the single fresh grounded impact is armed.
    // The rest of grounded and all takeoff time are harmless recovery and must
    // not hold ordinary attacks hostage.
    this.eaglePriority = (world.eagles ?? [])
      .filter((e) => e.state === 'high' || e.state === 'low'
        || (e.state === 'grounded' && e._talonCommitFresh)).length;
    this.majorCapacity = this.bossPriority ? 0
      : Math.max(0, this.profile.majorCap - Number(this.eaglePriority > 0));
    this.releaseHarmlessTokens(world);
    this.majorFrameGrants.clear();
    this.majorArbitrationFrame = this.frameSerial;
    if (this.bossPriority || this.eaglePriority > 0) {
      this.preemptMajorTells(world);
      this.preemptMinorTells(world.t);
    } else if (this.tokens.size > this.majorCapacity) {
      // A tell begun in the prior band cannot leak into the authored lull. Already
      // committed paths finish; only still-readable tells are withdrawn.
      this.preemptMajorTells(world);
    }
    if (this.tokens.size > 0) this.preemptMinorTells(world.t);
    this.majorRecoveryActive = this.hasMajorRecovery(world);
    // Recovery is still a visible part of the previous major phrase. Its token
    // is returned for ordinary attacks, but a second global warning does not
    // immediately chain over that opening and erase it.
    if (!this.majorRecoveryActive) this.startReadyMajorsFair(world);
    this.enemyMajorActive = this.hasEnemyMajorAttack(world);
    this.minorCapacity = (this.bossPriority || this.eaglePriority > 0
      || this.enemyMajorActive) ? 0 : GROUND_ATTACK.localCap;
    this.prepareMinorCandidates(world, this.frameDt);
  }

  hasBossPriority(world) {
    return !!(world.boss && !world.boss.dead && (
      world.boss.target || world.boss.state === 'tell'
      || world.boss.attackGeometry?.stage === 'signal'
    ));
  }

  eagleActionActive(world) {
    return (world.eagles ?? []).some((e) => e.state === 'high'
      || e.state === 'low' || e.state === 'grounded');
  }

  canStartEagle(world) {
    // A scheduled eagle only waits for an attack that has already locked.  An idle
    // boss never starves the schedule: bossStepDt advances its gauge to (not across)
    // the lock boundary and gives the fixed eagle warning+impact window priority.
    return !this.eagleActionActive(world) && !this.hasBossPriority(world);
  }

  noteEagleDelay(delaySec) {
    const delay = Math.max(0, Number(delaySec) || 0);
    if (delay <= EPS) return;
    this.metrics.eagleDelaySec += delay;
    this.metrics.maxEagleDelaySec = Math.max(this.metrics.maxEagleDelaySec, delay);
  }

  bossStepDt(world, dt) {
    const b = world.boss;
    const baseState = b?.kind === 'lion' ? 'active' : 'roam';
    if (!b || b.dead || b.target || b.state !== baseState || !this.eagleActionActive(world)) {
      return dt;
    }
    const requested = Math.max(0, Number(dt) || 0);
    const chargeSec = Math.max(EPS, Number(b.attackPattern?.chargeSec) || EPS);
    const toLock = Math.max(0, (1 - (Number(b.attackGauge) || 0)) * chargeSec);
    // Keep normal chase/gauge motion until the exact lock boundary; only the time that
    // would cross that boundary is held.  This serializes tells without freezing the
    // boss for the eagle's whole 6.5-second warning+impact window.
    const allowed = Math.min(requested, Math.max(0, toLock - EPS));
    this.metrics.bossHeldSec += requested - allowed;
    return allowed;
  }

  afterBossStep(world) {
    this.bossPriority = this.hasBossPriority(world);
    if (this.bossPriority) {
      this.preemptMajorTells(world);
      this.preemptMinorTells(world.t);
    }
    // Boss nights sample once more after its FSM advances.  Duration belongs to
    // this final state; the pre-boss sample in moveEnemies is geometry/max-only.
    this.endFrame(world, true);
  }

  reconcile(world) {
    const enemies = world.enemies ?? [];
    const eagles = world.eagles ?? [];
    let groundMismatch = this.activeEnemies.size !== enemies.length;
    if (!groundMismatch) {
      for (const e of enemies) if (!this.activeEnemies.has(e)) { groundMismatch = true; break; }
    }
    if (groundMismatch) {
      const ground = new Set(enemies);
      for (const e of [...this.activeEnemies]) if (!ground.has(e)) this.removeEnemy(e);
    }
    let aerialMismatch = this.activeEagles.size !== eagles.length;
    if (!aerialMismatch) {
      for (const e of eagles) if (!this.activeEagles.has(e)) { aerialMismatch = true; break; }
    }
    if (aerialMismatch) {
      const aerial = new Set(eagles);
      for (const e of [...this.activeEagles]) if (!aerial.has(e)) this.removeEnemy(e);
    }
  }

  endFrame(world, accountDuration = !world.boss) {
    this.enemyMajorActive = this.hasEnemyMajorAttack(world);
    const enemyTells = (world.enemies ?? []).reduce((n, e) => n
      + Number(!!e.behaviorMajor && e.behaviorState === 'tell'), 0);
    const bossTell = Number(world.boss?.state === 'tell'
      || world.boss?.attackGeometry?.stage === 'signal');
    const total = bossTell + this.eagleWarningCount + enemyTells;
    this.metrics.majorTells = total;
    this.metrics.maxMajorTells = Math.max(this.metrics.maxMajorTells, total);
    this.metrics.maxEnemyMajorTells = Math.max(this.metrics.maxEnemyMajorTells, enemyTells);
    this.metrics.minorAttacks = this.minorTokens.size;
    this.metrics.maxMinorAttacks = Math.max(this.metrics.maxMinorAttacks,
      this.minorTokens.size);
    if (accountDuration) {
      const frameSec = Math.max(0, Number(this.frameDt) || 0);
      if (total > 0) this.metrics.majorSec += frameSec;
      if (enemyTells > 0) this.metrics.enemyMajorSec += frameSec;
      if (this.eagleWarningCount > 0) this.metrics.eagleMajorSec += frameSec;
      if (bossTell > 0) this.metrics.bossMajorSec += frameSec;
    }
    if ((bossTell > 0 || this.eaglePriority > 0) && enemyTells > 0) {
      this.metrics.priorityOverlapFrames++;
    }
    this.minorFrameGrants.clear();
    this.minorArbitrationFrame = -1;
    this.majorFrameGrants.clear();
    this.majorArbitrationFrame = -1;
  }

  hasEnemyMajorAttack(world) {
    if (this.tokens.size > 0) return true;
    return (world.enemies ?? []).some((e) => e._behaviorAlive
      && (e.behaviorToken === 'hyena' || e.behaviorToken === 'cohort'
        || e.behaviorMajor)
      && (e.behaviorState === 'tell' || e.behaviorState === 'commit'));
  }

  hasMajorRecovery(world) {
    if ((world.eagles ?? []).some((e) => e.state === 'grounded'
        && !e._talonCommitFresh)) return true;
    return (world.enemies ?? []).some((e) => e._behaviorAlive
      && e.behaviorState === 'recover'
      && (e.species === 'hyena' || e.species === 'crow'
        || e.attackKind === 'alternating-bite'
        || e.attackKind === 'pack-command'));
  }

  releaseCohortTokenIfHarmless(cohort) {
    if (!cohort) return false;
    const dangerous = [...cohort.members].some((member) => member._behaviorAlive
      && (member.behaviorState === 'tell' || member.behaviorState === 'commit'));
    if (dangerous) return false;
    this.release(cohort);
    for (const member of cohort.members) {
      if (!member._behaviorAlive || member.behaviorState !== 'recover') continue;
      member.behaviorToken = null;
      member.behaviorMajor = false;
    }
    return true;
  }

  releaseHarmlessTokens(world) {
    for (const e of world.enemies ?? []) {
      if (!e._behaviorAlive || e.behaviorState !== 'recover') continue;
      if (e.behaviorMinor || this.minorTokens.has(e)) {
        this.releaseMinor(e);
        e.behaviorMinor = false;
        if (e.behaviorToken === 'minor') e.behaviorToken = null;
      }
      if (e.behaviorToken === 'hyena' || this.tokens.has(e)) {
        this.release(e);
        e.behaviorToken = null;
        e.behaviorMajor = false;
      }
    }
    for (const cohort of this.cohorts.values()) {
      if (this.tokens.has(cohort)) this.releaseCohortTokenIfHarmless(cohort);
    }
  }

  preemptMajorTells(world) {
    for (const owner of [...this.tokens.keys()]) {
      if (owner?.members instanceof Set) {
        const telling = [...owner.members].filter((e) => e._behaviorAlive
          && e.behaviorState === 'tell');
        if (!telling.length) continue;
        // Staggered packs can have one body already committed while the next is
        // still loading. Priority withdraws unreadied tells, never a physical
        // path that has already launched.
        for (const member of telling) {
          this.cancelAction(member, world.t, { preempt: true });
        }
        const launched = [...owner.members].filter((member) => member._behaviorAlive
          && (member.behaviorState === 'commit' || member.behaviorState === 'recover'));
        owner.primary = launched.find((member) => member.behaviorState === 'commit'
          && member.behaviorTargetX != null) ?? launched[0] ?? null;
        if (!launched.length) {
          owner.active = false;
          owner.contactSpent = true;
        }
        this.release(owner);
      } else if (owner?._behaviorAlive && owner.behaviorState === 'tell') {
        this.cancelAction(owner, world.t, { preempt: true });
      }
    }
  }

  preemptMinorTells(now = this.now) {
    for (const e of [...this.minorTokens.keys()]) {
      // A committed physical motion finishes its real path. Only an attack that
      // is still visibly loading yields to the major warning.
      if (!e?._behaviorAlive || e.behaviorState !== 'tell') continue;
      this.count(e, 'minorPreempt');
      this.abortMinor(e, now);
    }
  }

  cancelAction(e, now, { preempt = false, recover = false } = {}) {
    if (!e) return;
    this.count(e, 'cancel');
    if (preempt) this.count(e, 'preempt');
    this.release(e);
    this.releaseMinor(e);
    e.behaviorSweep = null;
    e._behaviorAvoidPending?.delete(e.behaviorSeq);
    e.behaviorCommitted = false;
    e.behaviorConnected = false;
    e.behaviorMajor = false;
    e.behaviorMinor = false;
    e.behaviorToken = null;
    e.contactArmed = false;
    e.tell = 0; e.dash = 0;
    clearGeometry(e);
    if (recover) {
      const tune = e.species === 'crow' ? BEHAVIOR_TUNE.crow : BEHAVIOR_TUNE.dog;
      e.behaviorState = 'recover';
      e.behaviorProgress = 0;
      e.behaviorAnchored = true;
      e.cohortCommand = 'recover';
      e.attackStage = 'recover';
      e._behaviorStateT = tune.recoverSec;
      e._behaviorStateDuration = tune.recoverSec;
      e._behaviorRecoverSec = tune.recoverSec;
    } else {
      e.behaviorState = e.behaviorKind === 'straight' ? 'fallback' : 'approach';
      e.behaviorProgress = 0;
      e.behaviorAnchored = false;
      e.contactArmed = false;
      e.cohortCommand = null;
    }
    e.behaviorNextAt = Math.max(e.behaviorNextAt || 0, now + ACT.charge.cool);
  }

  cancelCohort(cohort, now, { preempt = false, recover = false } = {}) {
    if (!cohort) return;
    for (const member of cohort.members) {
      if (!member._behaviorAlive) continue;
      if (member.behaviorState === 'tell' || member.behaviorState === 'commit'
          || member.behaviorState === 'recover') {
        this.cancelAction(member, now, { preempt, recover });
      }
    }
    cohort.active = false;
    cohort.primary = null;
    cohort.contactSpent = true;
    cohort.nextAt = Math.max(cohort.nextAt || 0, now + ACT.charge.cool);
    this.release(cohort);
  }

  acquire(owner, kind) {
    if (this.tokens.has(owner)) return true;
    if (this.bossPriority || this.tokens.size >= this.majorCapacity) {
      this.metrics.tokenDenied++;
      return false;
    }
    this.tokens.set(owner, kind);
    this.metrics.tokenGrants++;
    this.preemptMinorTells(this.now);
    return true;
  }

  release(owner) {
    if (owner) this.tokens.delete(owner);
  }

  acquireMinor(owner, kind) {
    if (this.minorTokens.has(owner)) return true;
    if (this.bossPriority || this.eaglePriority > 0 || this.enemyMajorActive
        || this.tokens.size > 0
        || this.minorTokens.size >= this.minorCapacity) {
      this.metrics.minorDenied++;
      return false;
    }
    if (this.minorArbitrationFrame === this.frameSerial
        && !this.minorFrameGrants.has(owner)) {
      this.metrics.minorDenied++;
      return false;
    }
    this.minorTokens.set(owner, kind);
    this.minorFrameGrants.delete(owner);
    this.metrics.minorGrants++;
    this.metrics.maxMinorAttacks = Math.max(this.metrics.maxMinorAttacks,
      this.minorTokens.size);
    return true;
  }

  releaseMinor(owner) {
    if (owner) this.minorTokens.delete(owner);
  }

  abortMinor(e, now = this.now) {
    if (!e) return;
    this.releaseMinor(e);
    e.behaviorSweep = null;
    e._behaviorAvoidPending?.delete(e.behaviorSeq);
    e.behaviorCommitted = false;
    e.behaviorConnected = false;
    e.behaviorMinor = false;
    e.behaviorToken = null;
    e.behaviorMajor = false;
    e.contactArmed = false;
    e.behaviorAnchored = false;
    e.attackStage = null;
    e._grassAttackCycle = false;
    e._batAttackCycle = false;
    clearGeometry(e);
    e.behaviorState = 'approach';
    e.behaviorProgress = 0;
    e.behaviorNextAt = Math.max(e.behaviorNextAt || 0,
      now + GRASSHOPPER.phaseSec);
  }

  selectFairCandidate(order, cursorKey, buckets) {
    const count = order.length;
    for (let offset = 0; offset < count; offset++) {
      const index = (this[cursorKey] + offset) % count;
      const bucket = buckets.get(order[index]);
      if (!bucket?.length) continue;
      let best = 0;
      for (let i = 1; i < bucket.length; i++) {
        const a = bucket[i], b = bucket[best];
        if (a.readyAt < b.readyAt - EPS
            || (Math.abs(a.readyAt - b.readyAt) <= EPS
              && a.ordinal < b.ordinal)) best = i;
      }
      const [candidate] = bucket.splice(best, 1);
      this[cursorKey] = (index + 1) % count;
      return candidate;
    }
    return null;
  }

  startReadyMajorsFair(world) {
    if (this.tokens.size > 0 || this.bossPriority || this.eaglePriority > 0
        || this.majorCapacity <= 0) return;
    const buckets = new Map(MAJOR_SPECIES_ORDER.map((species) => [species, []]));
    for (const cohort of this.cohorts.values()) {
      if (cohort.active || world.t + EPS < cohort.nextAt) continue;
      const members = [...cohort.members].filter((e) => e._behaviorAlive).slice(0,
        cohort.type === 'dog' ? BEHAVIOR_TUNE.dog.maxMembers : BEHAVIOR_TUNE.crow.maxMembers);
      const eligible = cohort.type === 'dog'
        ? this.profile.dog && cohort.leader?._behaviorAlive && members.length >= 2
        : this.profile.crow && members.length >= 2;
      if (!eligible) continue;
      const species = cohort.type === 'dog' ? 'wild-dog' : 'crow';
      buckets.get(species).push({
        type: 'cohort', owner: cohort, members,
        readyAt: cohort.nextAt, ordinal: cohort.id,
      });
    }
    for (const e of world.enemies ?? []) {
      if (e.species !== 'hyena' || !e._behaviorAlive) continue;
      if (e.behaviorState === 'tell' || e.behaviorState === 'commit'
          || e.behaviorState === 'recover') continue;
      const v = norm(world.player.x - e.x, world.player.y - e.y);
      if (!this.profile.hyena || v.d >= ACT.charge.range || e.cool > 0) continue;
      buckets.get('hyena').push({
        type: 'hyena', owner: e, readyAt: world.t + Math.min(0, e.cool || 0),
        ordinal: e._behaviorOrdinal,
      });
    }
    const candidate = this.selectFairCandidate(
      MAJOR_SPECIES_ORDER, 'majorSpeciesCursor', buckets,
    );
    if (!candidate) return;
    const unselected = [...buckets.values()].reduce((sum, bucket) =>
      sum + bucket.length, 0);
    this.metrics.tokenDenied += unselected;
    if (candidate.type === 'cohort') {
      if (!this.acquire(candidate.owner, 'cohort')) return;
      this.startCohort(world, candidate.owner, candidate.members);
    } else {
      this.majorFrameGrants.add(candidate.owner);
      this.tryStartHyena(candidate.owner, world);
    }
  }

  minorCandidateReady(e, world, dt) {
    if (!e?._behaviorAlive || e.behaviorState === 'tell'
        || e.behaviorState === 'commit' || e.behaviorState === 'recover'
        || world.t + EPS < (e.behaviorNextAt || 0)) return false;
    const p = world.player;
    if (e.species === 'field-rat' || e.species === 'wolf'
        || e.species === 'wild-dog') {
      if (e.species === 'wild-dog' && (e.cohortId != null || !this.profile.dog)) {
        return false;
      }
      const tune = e.species === 'field-rat' ? GROUND_ATTACK.rat
        : e.species === 'wolf' ? GROUND_ATTACK.wolf : GROUND_ATTACK.dog;
      return Math.hypot(p.x - e.x, p.y - e.y)
        <= PLAYER.r + e.r + tune.distance + EPS;
    }
    if (e.species === 'jackal') {
      if (!this.profile.jackal) return false;
      const tx = p.x + (p.vx || 0) * BEHAVIOR_TUNE.jackal.leadSec;
      const ty = p.y + (p.vy || 0) * BEHAVIOR_TUNE.jackal.leadSec;
      return Math.hypot(tx - e.x, ty - e.y)
        <= PLAYER.r + e.r + BEHAVIOR_TUNE.jackal.pathDistance + EPS;
    }
    if (e.species === 'grasshopper') {
      if (e._grassAttackCycle || (e.motionState ?? 'idle') !== 'idle') return false;
      const toCompress = GRASSHOPPER.phaseSec - (e.motionT || 0);
      if (dt + EPS < toCompress) return false;
      const distance = Math.max(Math.hypot(p.x - e.x, p.y - e.y),
        enemyNeedsStandoff(e) ? enemyStandoffRadius(e) : 0);
      return distance <= ENEMY.swarm.speed * GRASSHOPPER.leapSpeedMul
        * GRASSHOPPER.phaseSec + EPS;
    }
    if (e.species === 'bat') {
      if (!this.profile.bat || !e._batCycleReady || e._batAttackCycle) return false;
      const tune = BEHAVIOR_TUNE.bat;
      const toBoundary = tune.cycleSec - (e.waveT || 0);
      if (dt + EPS < toBoundary) return false;
      const phase0 = (e.waveT || 0) / tune.cycleSec * TAU;
      const bx = e._waveBaseDx || 1, by = e._waveBaseDy || 0;
      const forward = ENEMY.swarm.speed * tune.forwardMul * toBoundary;
      const lateral = tune.amplitude * (Math.sin(TAU) - Math.sin(phase0)) * e.waveSide;
      const x = e.x + bx * forward + (-by) * lateral;
      const y = e.y + by * forward + bx * lateral;
      return Math.hypot(p.x - x, p.y - y)
        <= PLAYER.r + e.r + ACT.charge.dist + EPS;
    }
    return false;
  }

  prepareMinorCandidates(world, dt) {
    this.minorFrameGrants.clear();
    this.minorArbitrationFrame = this.frameSerial;
    const available = Math.max(0, Math.min(GROUND_ATTACK.localCap,
      this.minorCapacity) - this.minorTokens.size);
    if (!available) return;
    const buckets = new Map(MINOR_SPECIES_ORDER.map((species) => [species, []]));
    for (const e of world.enemies ?? []) {
      if (!this.minorCandidateReady(e, world, dt)) continue;
      const species = e.species === 'wild-dog' ? 'wild-dog' : e.species;
      const bucket = buckets.get(species);
      if (!bucket) continue;
      bucket.push({
        owner: e, readyAt: Number(e.behaviorNextAt) || 0,
        ordinal: e._behaviorOrdinal,
      });
    }
    for (let slot = 0; slot < available; slot++) {
      const candidate = this.selectFairCandidate(
        MINOR_SPECIES_ORDER, 'minorSpeciesCursor', buckets,
      );
      if (!candidate) break;
      this.minorFrameGrants.add(candidate.owner);
    }
  }

  startCohort(world, cohort, members) {
    const crow = cohort.type === 'crow';
    const tune = crow ? BEHAVIOR_TUNE.crow : BEHAVIOR_TUNE.dog;
    // The dog leader commands but does not become the invisible 100-damage
    // lottery winner of a pack bite. Only ordinary dogs take bite lanes; the
    // first real lane owns the single major path. Crows all take lanes.
    const attackers = crow ? members : members.filter((member) => member !== cohort.leader);
    if (!attackers.length) {
      this.release(cohort);
      cohort.nextAt = world.t + tune.cooldownSec;
      return;
    }
    const primary = attackers[0];
    const p = world.player;
    cohort.commandSeq = (cohort.commandSeq ?? 0) + 1;
    cohort.contactSpent = false;
    const lead = tune.leadSec;
    const px = p.x + (p.vx || 0) * lead;
    const py = p.y + (p.vy || 0) * lead;
    const middle = (attackers.length - 1) / 2;
    let longestSec = 0;
    for (let i = 0; i < members.length; i++) {
      const e = members[i];
      const attackIndex = attackers.indexOf(e);
      const leader = !crow && e === cohort.leader;
      e.behaviorState = 'tell'; e.behaviorProgress = 0; e.behaviorSeq++;
      e.behaviorToken = 'cohort'; e.behaviorMajor = e === primary;
      e.behaviorMinor = false;
      e.behaviorAnchored = true; e.contactArmed = false; e.behaviorConnected = false;
      e.behaviorCommitted = false;
      e.attackKind = crow ? 'staggered-peck' : leader ? 'pack-command' : 'alternating-bite';
      e.attackStage = 'tell';
      e.cohortCommand = 'rally';
      e.cohortCommandSeq = cohort.commandSeq;
      if (leader) {
        clearGeometry(e);
        e._behaviorStateT = tune.tellSec;
        e._behaviorStateDuration = tune.tellSec;
        e._behaviorCommitSpeed = 0;
        e._behaviorRecoverSec = tune.recoverSec;
        e._behaviorLeaderHold = true;
        this.count(e, 'start');
        this.metrics.bandStarts[this.band]++;
        continue;
      }
      const toward = norm(px - e.x, py - e.y);
      const side = crow ? cohort.laneSide : 1;
      const slot = (attackIndex - middle) * tune.slotGap;
      const tx = px + (-toward.y) * slot * side;
      const ty = py + toward.x * slot * side;
      const speed = ENEMY[e.kind].speed * tune.speedMul;
      lockPath(e, tx, ty, tune.pathDistance);
      hideTellPath(e, tune.pathDistance);
      const delay = attackIndex * tune.staggerSec;
      e._behaviorStateT = tune.tellSec + delay;
      e._behaviorStateDuration = tune.tellSec + delay;
      e._behaviorCommitSpeed = speed; e._behaviorRecoverSec = tune.recoverSec;
      if (crow) { e.laneId = cohort.id; e.laneSide = cohort.laneSide; }
      this.count(e, 'start');
      this.metrics.bandStarts[this.band]++;
      const commitSec = e.behaviorDistance / Math.max(EPS, speed);
      longestSec = Math.max(longestSec, tune.tellSec + delay + commitSec
        + tune.recoverSec);
    }
    for (const e of members) {
      if (e._behaviorLeaderHold) {
        e._behaviorLeaderRecoverSec = Math.max(tune.recoverSec,
          longestSec - tune.tellSec);
      }
    }
    cohort.active = true;
    cohort.primary = primary;
    cohort.nextAt = world.t + longestSec + tune.cooldownSec;
  }

  moveEnemy(e, dt, world) {
    if (!e?._behaviorInitialized) this.initializeEnemy(e, world.t);
    switch (e.species) {
      case 'field-rat': return this.stepLocalLunge(e, dt, world,
        GROUND_ATTACK.rat, 'bite-retreat');
      case 'wolf': return this.stepLocalLunge(e, dt, world,
        GROUND_ATTACK.wolf, 'orbit-lunge');
      case 'hyena': return this.stepHyena(e, dt, world);
      case 'jackal': return this.stepJackal(e, dt, world);
      case 'bat': return this.stepBat(e, dt, world);
      case 'wild-dog': {
        if (e.behaviorState === 'tell' || e.behaviorState === 'commit'
            || e.behaviorState === 'recover') {
          return e.behaviorMinor
            ? this.stepLocalLunge(e, dt, world, GROUND_ATTACK.dog, 'solo-bite')
            : this.stepCohortMember(e, dt, world);
        }
        if (e.cohortId != null || !this.profile.dog) return false;
        return this.stepLocalLunge(e, dt, world, GROUND_ATTACK.dog, 'solo-bite');
      }
      case 'wild-dog-leader':
        return this.stepCohortMember(e, dt, world);
      case 'crow': {
        if (e.behaviorState === 'tell' || e.behaviorState === 'commit'
            || e.behaviorState === 'recover') return this.stepCohortMember(e, dt, world);
        return false;
      }
      default: return false;
    }
  }

  startLocalLunge(e, world, tune, attackKind) {
    if (world.t + EPS < e.behaviorNextAt) return false;
    const p = world.player;
    const v = norm(p.x - e.x, p.y - e.y, e.behaviorDx || 1, e.behaviorDy || 0);
    const trigger = PLAYER.r + e.r + tune.distance;
    if (v.d > trigger || !this.acquireMinor(e, attackKind)) return false;
    lockPath(e, p.x, p.y, tune.distance);
    hideTellPath(e, tune.distance);
    e.behaviorState = 'tell'; e.behaviorProgress = 0; e.behaviorSeq++;
    e.behaviorToken = 'minor'; e.behaviorMajor = false; e.behaviorMinor = true;
    e.attackKind = attackKind; e.attackStage = 'tell';
    e.behaviorAnchored = true; e.contactArmed = false;
    e.behaviorConnected = false; e.behaviorCommitted = false;
    e._behaviorStateT = tune.tellSec; e._behaviorStateDuration = tune.tellSec;
    e._behaviorCommitSpeed = ENEMY[e.kind].speed * tune.speedMul;
    e._behaviorRecoverSec = tune.recoverSec;
    e._behaviorRetreatDistance = tune.retreatDistance;
    e._behaviorRetreatRemaining = tune.retreatDistance;
    e._behaviorCooldownSec = tune.cooldownSec;
    this.count(e, 'start'); this.metrics.bandStarts[this.band]++;
    return true;
  }

  stepLocalLunge(e, dt, world, tune, attackKind) {
    if (e.behaviorState !== 'tell' && e.behaviorState !== 'commit'
        && e.behaviorState !== 'recover') {
      if (!this.startLocalLunge(e, world, tune, attackKind)) return false;
    }
    let left = Math.max(0, dt);
    while (left > EPS) {
      if (e.behaviorState === 'tell') {
        trackTellFacing(e, world.player);
        const slice = Math.min(left, e._behaviorStateT);
        e._behaviorStateT -= slice; left -= slice;
        e.behaviorProgress = 1 - e._behaviorStateT
          / Math.max(EPS, e._behaviorStateDuration);
        if (e._behaviorStateT > EPS) break;
        lockCommitAimRay(e, world.player, dt, dt - left, tune.distance);
        e.behaviorState = 'commit'; e.behaviorProgress = 0;
        e.behaviorAnchored = false; e.contactArmed = true;
        e.behaviorCommitted = true; e.attackStage = 'commit';
        e._behaviorStateT = e.behaviorDistance
          / Math.max(EPS, e._behaviorCommitSpeed);
        e._behaviorStateDuration = Math.max(EPS, e._behaviorStateT);
        this.count(e, 'commit');
        if (e.behaviorDistance <= EPS) {
          const at = dt - left;
          const reach = Math.max(1, e.r || 0);
          recordBehaviorSweep(e, e.x, e.y,
            e.x + (e.behaviorDx || 1) * reach,
            e.y + (e.behaviorDy || 0) * reach,
            at, at, e.behaviorSeq,
            { attackKind, instant: true, armed: true });
        }
      } else if (e.behaviorState === 'commit') {
        const slice = Math.min(left, e._behaviorStateT);
        const timeFrom = dt - left;
        lockedMove(e, e._behaviorCommitSpeed * slice,
          timeFrom, timeFrom + slice, { attackKind });
        e._behaviorStateT -= slice; left -= slice;
        if (e._behaviorStateT > EPS) break;
        e.behaviorState = 'recover'; e.behaviorProgress = 0;
        e.behaviorAnchored = false; e.contactArmed = false;
        e.attackStage = 'recover';
        this.releaseMinor(e);
        e.behaviorMinor = false; e.behaviorToken = null;
        e._behaviorStateT = e._behaviorRecoverSec;
        e._behaviorStateDuration = Math.max(EPS, e._behaviorRecoverSec);
      } else if (e.behaviorState === 'recover') {
        const slice = Math.min(left, e._behaviorStateT);
        const before = e._behaviorStateT;
        const fraction = before > EPS ? slice / before : 1;
        const retreat = Math.min(e._behaviorRetreatRemaining || 0,
          (e._behaviorRetreatRemaining || 0) * fraction);
        const fromX = e.x, fromY = e.y;
        e.x -= (e.behaviorDx || 0) * retreat;
        e.y -= (e.behaviorDy || 0) * retreat;
        recordBehaviorSweep(e, fromX, fromY, e.x, e.y,
          dt - left, dt - left + slice, e.behaviorSeq,
          { armed: false, attackKind, retreat: true });
        e._behaviorRetreatRemaining = Math.max(0,
          (e._behaviorRetreatRemaining || 0) - retreat);
        e._behaviorStateT -= slice; left -= slice;
        e.behaviorProgress = 1 - e._behaviorStateT
          / Math.max(EPS, e._behaviorStateDuration);
        if (e._behaviorStateT > EPS) break;
        const cooldown = e._behaviorCooldownSec;
        this.finishAction(e, world.t);
        e.behaviorNextAt = Math.max(e.behaviorNextAt || 0, world.t + cooldown);
        if (left > EPS) {
          this.moveFallback(e, left, world);
        }
        return true;
      } else return false;
    }
    return true;
  }

  stepHyena(e, dt, world) {
    const A = ACT.charge;
    if (e._hyenaCoolFrame !== this.frameSerial) {
      e.cool = (e.cool ?? 0) - dt;
      e._hyenaCoolFrame = this.frameSerial;
    }
    if (e.behaviorState !== 'tell' && e.behaviorState !== 'commit'
        && e.behaviorState !== 'recover') {
      if (!this.tryStartHyena(e, world)) return false;
    }
    let left = Math.max(0, dt);
    while (left > EPS) {
      if (e.behaviorState === 'tell') {
        trackTellFacing(e, world.player);
        const slice = Math.min(left, e._behaviorStateT);
        e._behaviorStateT -= slice; e.tell = Math.max(0, e._behaviorStateT); left -= slice;
        e.behaviorProgress = 1 - e._behaviorStateT / e._behaviorStateDuration;
        if (e._behaviorStateT > EPS) break;
        lockCommitAimRay(e, world.player, dt, dt - left, A.dist);
        e.dx = e.behaviorDx; e.dy = e.behaviorDy;
        e.behaviorState = 'commit'; e.behaviorMajor = false; e.behaviorAnchored = false;
        e.contactArmed = true; e.dash = A.dist;
        e.attackStage = 'commit';
        e.behaviorCommitted = true;
        e._behaviorStateT = A.dist / (ENEMY.hyena.speed * A.mult);
        e._behaviorStateDuration = e._behaviorStateT;
        this.count(e, 'commit');
      } else if (e.behaviorState === 'commit') {
        const speed = ENEMY.hyena.speed * A.mult;
        const slice = Math.min(left, e._behaviorStateT);
        const timeFrom = dt - left;
        lockedMove(e, speed * slice, timeFrom, timeFrom + slice);
        e.dash = e._behaviorRemaining;
        e._behaviorStateT -= slice; left -= slice;
        if (e._behaviorStateT > EPS) break;
        e.behaviorState = 'recover'; e.behaviorProgress = 0; e.dash = 0;
        e.attackStage = 'recover';
        e.recover = BEHAVIOR_TUNE.hyena.recoverSec;
        e._behaviorStateT = e.recover; e._behaviorStateDuration = e.recover;
        e.behaviorAnchored = true; e.contactArmed = false;
        this.release(e);
        e.behaviorToken = null; e.behaviorMajor = false;
      } else if (e.behaviorState === 'recover') {
        const slice = Math.min(left, e._behaviorStateT);
        e._behaviorStateT -= slice; e.recover = Math.max(0, e._behaviorStateT); left -= slice;
        e.behaviorProgress = 1 - e._behaviorStateT / e._behaviorStateDuration;
        if (e._behaviorStateT > EPS) break;
        this.finishAction(e, world.t);
        if (left > EPS) {
          this.moveFallback(e, left, world);
        }
        return true;
      } else return false;
    }
    return true;
  }

  tryStartHyena(e, world) {
    const A = ACT.charge;
    const p = world.player;
    const v = norm(p.x - e.x, p.y - e.y);
    if (!this.profile.hyena || v.d >= A.range || e.cool > 0) return false;
    if (this.majorArbitrationFrame === this.frameSerial
        && !this.majorFrameGrants.has(e)) {
      this.metrics.tokenDenied++;
      return false;
    }
    if (!this.acquire(e, 'hyena')) return false;
    this.majorFrameGrants.delete(e);
    e.behaviorState = 'tell'; e.behaviorProgress = 0; e.behaviorSeq++;
    e.behaviorToken = 'hyena'; e.behaviorMajor = true;
    e.behaviorMinor = false; e.attackKind = 'frontal-bite'; e.attackStage = 'tell';
    e.behaviorDx = v.x; e.behaviorDy = v.y; e.dx = v.x; e.dy = v.y;
    e.behaviorTargetX = e.x + v.x * A.dist;
    e.behaviorTargetY = e.y + v.y * A.dist;
    e.behaviorDistance = A.dist;
    e._behaviorRemaining = A.dist; e._behaviorTravelled = 0;
    hideTellPath(e, A.dist);
    e.tell = A.tell; e.dash = 0; e.recover = 0; e.cool = A.cool;
    e._behaviorStateT = A.tell; e._behaviorStateDuration = A.tell;
    e.behaviorAnchored = true; e.contactArmed = false; e.behaviorConnected = false;
    e.behaviorCommitted = false;
    this.count(e, 'start'); this.metrics.bandStarts[this.band]++;
    return true;
  }

  stepJackal(e, dt, world) {
    const tune = BEHAVIOR_TUNE.jackal;
    if (e.behaviorState !== 'commit' && e.behaviorState !== 'recover') {
      if (!this.profile.jackal || world.t + EPS < e.behaviorNextAt) return false;
      const p = world.player;
      const tx = p.x + (p.vx || 0) * tune.leadSec;
      const ty = p.y + (p.vy || 0) * tune.leadSec;
      const toward = norm(tx - e.x, ty - e.y, e.behaviorDx || 1, e.behaviorDy || 0);
      const trigger = PLAYER.r + e.r + tune.pathDistance;
      if (toward.d > trigger || !this.acquireMinor(e, 'crossing-snap')) return false;
      const speed = ENEMY.swarm.speed * tune.speedMul;
      // The jaw crosses the predicted point by one body length when there is
      // enough authored path left. Farther intercepts still respect the same
      // three-unit maximum and never grow a second range table.
      lockPath(e, tx + toward.x * tune.overshoot,
        ty + toward.y * tune.overshoot, tune.pathDistance);
      e.behaviorState = 'commit'; e.behaviorProgress = 0; e.behaviorSeq++;
      e.behaviorToken = 'minor'; e.behaviorMajor = false; e.behaviorMinor = true;
      e.attackKind = 'crossing-snap'; e.attackStage = 'commit'; e.contactArmed = true;
      e.behaviorConnected = false; e.behaviorCommitted = true;
      e._behaviorStateT = e.behaviorDistance / speed;
      e._behaviorStateDuration = Math.max(EPS, e._behaviorStateT);
      e._behaviorCommitSpeed = speed;
      this.count(e, 'start'); this.count(e, 'commit'); this.metrics.bandStarts[this.band]++;
    }
    let left = Math.max(0, dt);
    while (left > EPS) {
      if (e.behaviorState === 'commit') {
        const slice = Math.min(left, e._behaviorStateT);
        const timeFrom = dt - left;
        lockedMove(e, e._behaviorCommitSpeed * slice, timeFrom, timeFrom + slice);
        e._behaviorStateT -= slice; left -= slice;
        if (e._behaviorStateT > EPS) break;
        e.behaviorState = 'recover'; e.behaviorProgress = 0;
        e.behaviorAnchored = true; e.contactArmed = false;
        e.attackStage = 'recover';
        this.releaseMinor(e);
        e.behaviorMinor = false; e.behaviorToken = null;
        e._behaviorStateT = tune.recoverSec; e._behaviorStateDuration = tune.recoverSec;
      } else if (e.behaviorState === 'recover') {
        const slice = Math.min(left, e._behaviorStateT);
        e._behaviorStateT -= slice; left -= slice;
        e.behaviorProgress = 1 - e._behaviorStateT / e._behaviorStateDuration;
        if (e._behaviorStateT > EPS) break;
        this.finishAction(e, world.t);
        e.behaviorNextAt = world.t + tune.cooldownSec;
        if (left > EPS) {
          this.moveFallback(e, left, world);
        }
        return true;
      } else return false;
    }
    return true;
  }

  stepBat(e, dt, world) {
    const tune = BEHAVIOR_TUNE.bat;
    if (!this.profile.bat) {
      this.completeCommit(e);
      this.releaseMinor(e);
      e.behaviorState = 'fallback'; e.behaviorProgress = 0; e.behaviorMajor = false;
      e.behaviorMinor = false; e.contactArmed = false; e._batAttackCycle = false;
      clearGeometry(e);
      return false;
    }
    if (!e._batCycleReady) {
      // New bats are admitted after player movement in this frame, so their
      // first authored wave begins from the actual current player position.
      this.lockBatBase(e, world.player);
      e._batCycleReady = true;
      e._batAttackCycle = false;
      e.behaviorState = 'approach';
      e.contactArmed = false;
    }
    let left = Math.max(0, dt);
    while (left > EPS) {
      // A sine wave is not its start-to-end chord.  Split only at its analytic
      // extrema/zero boundaries (quarter-cycle), so the swept polyline follows the
      // same authored curve without a sampled or magic fixed timestep.
      const quarter = tune.cycleSec / 4;
      this.syncBatAttackStage(e, world, quarter, dt, dt - left);
      const boundary = Math.min(tune.cycleSec,
        (Math.floor((e.waveT + EPS) / quarter) + 1) * quarter);
      const slice = Math.min(left, Math.max(EPS, boundary - e.waveT));
      const t0 = e.waveT, t1 = e.waveT + slice;
      const a0 = t0 / tune.cycleSec * TAU, a1 = t1 / tune.cycleSec * TAU;
      const forward = ENEMY.swarm.speed * tune.forwardMul * slice;
      const lateral = tune.amplitude * (Math.sin(a1) - Math.sin(a0)) * e.waveSide;
      const bx = e._waveBaseDx, by = e._waveBaseDy;
      const fromX = e.x, fromY = e.y;
      e.x += bx * forward + (-by) * lateral;
      e.y += by * forward + bx * lateral;
      const timeFrom = dt - left;
      recordBehaviorSweep(
        e, fromX, fromY, e.x, e.y, timeFrom, timeFrom + slice, e.behaviorSeq,
        {
          curve: 'bat-sine', baseDx: bx, baseDy: by,
          phaseT0: t0, phaseT1: t1, cycleSec: tune.cycleSec,
          forwardSpeed: ENEMY.swarm.speed * tune.forwardMul,
          amplitude: tune.amplitude, side: e.waveSide,
          armed: e._batAttackCycle && e.behaviorState === 'commit',
          attackKind: 'swoop-bite',
        },
      );
      e.waveT = t1; e.wavePhase = a1 % TAU;
      const lateralV = tune.amplitude * (TAU / tune.cycleSec) * Math.cos(a1) * e.waveSide;
      const v = norm(bx * ENEMY.swarm.speed * tune.forwardMul + (-by) * lateralV,
                     by * ENEMY.swarm.speed * tune.forwardMul + bx * lateralV, bx, by);
      e.behaviorDx = v.x; e.behaviorDy = v.y;
      this.updateBatStageProgress(e, quarter);
      left -= slice;
      if (e.waveT + EPS < tune.cycleSec) {
        if (left <= EPS) break;
        continue;
      }
      if (e._batAttackCycle && e.behaviorState === 'commit') {
        this.completeCommit(e);
      }
      this.releaseMinor(e);
      e.waveT = 0; e.wavePhase = 0; e.waveSide *= -1;
      e.behaviorProgress = 0;
      const cycleBoundary = playerAtCommitBoundary(
        world.player, dt, dt - left,
      );
      this.lockBatBase(e, cycleBoundary);
      this.startBatCycle(e, world, cycleBoundary);
    }
    return true;
  }

  startBatCycle(e, world, targetPoint = world.player) {
    const p = targetPoint;
    const d = Math.hypot(p.x - e.x, p.y - e.y);
    const trigger = PLAYER.r + e.r + ACT.charge.dist;
    const ready = world.t + EPS >= e.behaviorNextAt && d <= trigger
      && this.acquireMinor(e, 'swoop-bite');
    e._batAttackCycle = ready;
    e._batCommitCounted = false;
    e.contactArmed = false;
    e.behaviorMajor = false;
    e.behaviorMinor = ready;
    e.behaviorToken = ready ? 'minor' : null;
    e.attackKind = ready ? 'swoop-bite' : null;
    e.attackStage = ready ? 'tell' : null;
    e.behaviorState = ready ? 'tell' : 'approach';
    e.behaviorProgress = 0;
    if (!ready) return;
    e.behaviorSeq++;
    e.behaviorConnected = false;
    e.behaviorCommitted = false;
    this.count(e, 'start'); this.metrics.bandStarts[this.band]++;
  }

  syncBatAttackStage(e, world, quarter, frameDt = 0, elapsedBoundary = 0) {
    if (!e._batAttackCycle) {
      e.behaviorState = 'approach'; e.attackStage = null;
      e.behaviorMinor = false; e.contactArmed = false;
      this.updateBatStageProgress(e, quarter);
      return;
    }
    const stage = e.waveT < quarter - EPS ? 'tell'
      : e.waveT < quarter * 3 - EPS ? 'commit' : 'recover';
    if (stage === 'commit' && !e._batCommitCounted) {
      const boundary = playerAtCommitBoundary(
        world.player, frameDt, elapsedBoundary,
      );
      if (boundary.moving) this.lockBatBase(e, boundary);
      e._batCommitCounted = true;
      e.behaviorCommitted = true;
      this.count(e, 'commit');
    } else if (stage === 'recover' && e.behaviorState === 'commit') {
      this.completeCommit(e);
      this.releaseMinor(e);
      e.behaviorNextAt = Math.max(e.behaviorNextAt || 0,
        world.t + GROUND_ATTACK.bat.cooldownSec);
    }
    e.behaviorState = stage;
    e.attackStage = stage;
    e.behaviorMinor = stage !== 'recover';
    if (stage === 'tell') trackTellFacing(e, world.player);
    // Curved segments own contact. The endpoint body never becomes a second
    // candidate after the same within-frame swoop has already passed.
    e.contactArmed = false;
    this.updateBatStageProgress(e, quarter);
  }

  updateBatStageProgress(e, quarter) {
    if (!e._batAttackCycle) {
      e.behaviorProgress = clamp01(e.waveT / BEHAVIOR_TUNE.bat.cycleSec);
    } else if (e.behaviorState === 'tell') {
      e.behaviorProgress = clamp01(e.waveT / quarter);
    } else if (e.behaviorState === 'commit') {
      e.behaviorProgress = clamp01((e.waveT - quarter) / (quarter * 2));
    } else {
      e.behaviorProgress = clamp01((e.waveT - quarter * 3) / quarter);
    }
  }

  lockBatBase(e, player) {
    const v = norm(player.x - e.x, player.y - e.y, e._waveBaseDx, e._waveBaseDy);
    e._waveBaseDx = v.x; e._waveBaseDy = v.y;
    e.bodyFacingDx = v.x; e.bodyFacingDy = v.y;
    e.behaviorTargetX = null; e.behaviorTargetY = null; e.behaviorDistance = 0;
  }

  stepCohortMember(e, dt, world) {
    if (e.behaviorState !== 'tell' && e.behaviorState !== 'commit'
        && e.behaviorState !== 'recover') return false;
    let left = Math.max(0, dt);
    while (left > EPS) {
      if (e.behaviorState === 'tell') {
        trackTellFacing(e, world.player);
        const slice = Math.min(left, e._behaviorStateT);
        e._behaviorStateT -= slice; left -= slice;
        e.behaviorProgress = 1 - e._behaviorStateT / e._behaviorStateDuration;
        if (e._behaviorStateT > EPS) break;
        if (e._behaviorLeaderHold) {
          e._behaviorLeaderHold = false;
          e.behaviorState = 'recover'; e.behaviorProgress = 0;
          e.behaviorMajor = false; e.behaviorAnchored = true;
          e.contactArmed = false; e.attackStage = 'recover';
          const remaining = Math.max(e._behaviorRecoverSec,
            e._behaviorLeaderRecoverSec || 0);
          e._behaviorStateT = remaining;
          e._behaviorStateDuration = Math.max(EPS, remaining);
          this.releaseCohortTokenIfHarmless(this.cohorts.get(e.cohortId));
          continue;
        }
        lockCommitAimRay(e, world.player, dt, dt - left,
          e._behaviorMaxTravel);
        e.behaviorState = 'commit'; e.behaviorProgress = 0;
        e.behaviorMajor = false; e.behaviorAnchored = false; e.contactArmed = true;
        e.behaviorCommitted = true;
        e.cohortCommand = 'rush';
        e.attackStage = 'commit';
        e._behaviorStateT = e.behaviorDistance / Math.max(EPS, e._behaviorCommitSpeed);
        e._behaviorStateDuration = Math.max(EPS, e._behaviorStateT);
        this.count(e, 'commit');
      } else if (e.behaviorState === 'commit') {
        const slice = Math.min(left, e._behaviorStateT);
        const timeFrom = dt - left;
        lockedMove(e, e._behaviorCommitSpeed * slice, timeFrom, timeFrom + slice);
        e._behaviorStateT -= slice; left -= slice;
        if (e._behaviorStateT > EPS) break;
        e.behaviorState = 'recover'; e.behaviorProgress = 0;
        e.cohortCommand = 'recover'; e.contactArmed = false; e.behaviorAnchored = true;
        e.attackStage = 'recover';
        e._behaviorStateT = e._behaviorRecoverSec; e._behaviorStateDuration = e._behaviorRecoverSec;
        this.releaseCohortTokenIfHarmless(this.cohorts.get(e.cohortId));
      } else if (e.behaviorState === 'recover') {
        const slice = Math.min(left, e._behaviorStateT);
        e._behaviorStateT -= slice; left -= slice;
        e.behaviorProgress = 1 - e._behaviorStateT / e._behaviorStateDuration;
        if (e._behaviorStateT > EPS) break;
        const cohort = this.cohorts.get(e.cohortId);
        this.finishAction(e, world.t);
        this.finishCohortIfIdle(cohort);
        if (left > EPS) {
          this.moveFallback(e, left, world);
        }
        return true;
      } else return false;
    }
    return true;
  }

  finishCohortIfIdle(cohort) {
    if (!cohort?.active) return;
    const busy = [...cohort.members].some((member) => member._behaviorAlive
      && (member.behaviorState === 'tell' || member.behaviorState === 'commit'
        || member.behaviorState === 'recover'));
    if (busy) return;
    cohort.active = false;
    cohort.primary = null;
    // Do not spend the latch before World.contact() consumes an armed segment
    // produced on this same hitch frame. A miss stays false; the next command
    // increments commandSeq and resets the latch explicitly.
    this.release(cohort);
  }

  finishAction(e, now) {
    this.completeCommit(e);
    this.release(e);
    this.releaseMinor(e);
    e.behaviorState = e.behaviorKind === 'straight' ? 'fallback' : 'approach';
    e.behaviorProgress = 0; e.behaviorToken = null; e.behaviorMajor = false;
    e.behaviorMinor = false; e.behaviorAnchored = false; e.contactArmed = false;
    e.cohortCommand = null; e.attackStage = null;
    e.bodyFacingDx = null; e.bodyFacingDy = null;
    e.tell = 0; e.dash = 0; e.recover = 0;
    e.behaviorConnected = false; e.behaviorCommitted = false;
    clearGeometry(e);
    e.behaviorNextAt = Math.max(e.behaviorNextAt || 0, now + GRASSHOPPER.phaseSec);
  }

  moveTowardPlayer(e, dt, player, speed) {
    const v = norm(player.x - e.x, player.y - e.y);
    e.x += v.x * speed * dt; e.y += v.y * speed * dt;
    e.behaviorDx = v.x; e.behaviorDy = v.y;
  }

  moveFallback(e, dt, world) {
    moveEnemyFallback(e, dt, world.player);
  }
}
